import { useRef, useState, type CSSProperties } from "react";
import { IconButton } from "@/components/ui/Button";
import { Icon, type IconName } from "@/components/ui/Icon";
import { Popover } from "@/components/ui/Popover";
import { RenameBoardDialog } from "@/components/dialogs/RenameBoardDialog";
import { DuplicateBoardDialog } from "@/components/dialogs/DuplicateBoardDialog";
import { ShareBoardDialog } from "@/components/dialogs/ShareBoardDialog";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { useDeleteBoard } from "@/hooks/useDeleteBoard";
import { useArchiveBoard, useUnarchiveBoard } from "@/hooks/useArchiveBoard";
import { useUpdateBoard } from "@/hooks/useUpdateBoard";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { BoardKind, UUID } from "@/lib/types";

interface Props {
  boardId: UUID;
  workspaceId: UUID;
  boardName: string;
  boardDescription?: string;
  kind: BoardKind;
  /** F2: render "Wiederherstellen" instead of "Archivieren" for archived boards. */
  archived?: boolean;
  /** Ideas boards: current Home-visibility, drives the publish toggle. */
  homeListed?: boolean;
}

export function BoardCardMenu({
  boardId,
  workspaceId,
  boardName,
  boardDescription = "",
  kind,
  archived = false,
  homeListed = false,
}: Props) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const deleteMutation = useDeleteBoard();
  const archiveMutation = useArchiveBoard();
  const unarchiveMutation = useUnarchiveBoard();
  const updateMutation = useUpdateBoard();

  async function onToggleHomeListed() {
    setOpen(false);
    try {
      await updateMutation.mutateAsync({
        boardId,
        workspaceId,
        payload: { home_listed: !homeListed },
      });
      showToast(homeListed ? t.ideaHomeHidden : t.ideaHomeShown, "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : t.unknownError, "error");
    }
  }

  async function onToggleArchive() {
    setOpen(false);
    try {
      if (archived) {
        await unarchiveMutation.mutateAsync({ boardId, workspaceId });
        showToast(t.boardUnarchived, "success");
      } else {
        await archiveMutation.mutateAsync({ boardId, workspaceId });
        showToast(t.boardArchived, "success");
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : t.unknownError, "error");
    }
  }

  async function onConfirmDelete() {
    try {
      await deleteMutation.mutateAsync({ boardId, workspaceId });
      setDeleteOpen(false);
      showToast(t.boardDeleted, "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : t.unknownError, "error");
    }
  }

  return (
    <>
      <IconButton
        ref={triggerRef}
        icon="dotsV"
        size={30}
        active={open}
        title={t.more}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        style={{
          background: open ? "var(--surface-elev)" : "var(--surface)",
          border: "1px solid var(--hairline)",
          boxShadow: "0 1px 2px rgba(13,62,99,0.06)",
        }}
      />
      {open ? (
        <Popover
          anchor={triggerRef.current}
          onClose={() => setOpen(false)}
          width={220}
          align="right"
        >
          <MenuItem
            icon="edit"
            label={t.renameBoard}
            onClick={() => {
              setOpen(false);
              setRenameOpen(true);
            }}
          />
          {kind === "project" ? (
            <MenuItem
              icon="copy"
              label={t.duplicateBoard}
              onClick={() => {
                setOpen(false);
                setDuplicateOpen(true);
              }}
            />
          ) : null}
          {kind === "tickets" || kind === "ideas" ? (
            <MenuItem
              icon="copy"
              label={t.submitLinkCopy}
              onClick={async () => {
                setOpen(false);
                const url = `${window.location.origin}/submit/${boardId}`;
                try {
                  await navigator.clipboard.writeText(url);
                  showToast(t.submitLinkCopied, "success");
                } catch {
                  showToast(t.copyLinkFail, "error");
                }
              }}
            />
          ) : null}
          {kind === "ideas" ? (
            <MenuItem
              icon="eye"
              label={homeListed ? t.ideaHomeHide : t.ideaHomeShow}
              onClick={onToggleHomeListed}
            />
          ) : null}
          <MenuItem
            icon="group"
            label={t.shareBoardMenuItem}
            onClick={() => {
              setOpen(false);
              setShareOpen(true);
            }}
          />
          <MenuItem
            icon={archived ? "inbox" : "archive"}
            label={archived ? t.boardUnarchive : t.boardArchive}
            onClick={onToggleArchive}
          />
          <Divider />
          <MenuItem
            icon="trash"
            label={t.deleteBoard}
            tone="danger"
            onClick={() => {
              setOpen(false);
              setDeleteOpen(true);
            }}
          />
        </Popover>
      ) : null}

      <RenameBoardDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        boardId={boardId}
        workspaceId={workspaceId}
        initialName={boardName}
        initialDescription={boardDescription}
      />

      {/* Mounted only while open — it calls useNavigate, which needs a router in scope. */}
      {duplicateOpen ? (
        <DuplicateBoardDialog
          open
          onOpenChange={setDuplicateOpen}
          boardId={boardId}
          workspaceId={workspaceId}
          boardName={boardName}
        />
      ) : null}

      <ShareBoardDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        boardId={boardId}
        homeWorkspaceId={workspaceId}
        boardName={boardName}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t.deleteBoardTitle}
        message={t.deleteBoardMessage.replace("{name}", boardName)}
        confirmLabel={t.deleteBoard}
        tone="danger"
        busy={deleteMutation.isPending}
        onConfirm={onConfirmDelete}
      />
    </>
  );
}

function Divider() {
  return <div style={{ height: 1, background: "var(--hairline)", margin: "4px 0" }} />;
}

function MenuItem({
  icon,
  label,
  onClick,
  tone = "default",
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
  tone?: "default" | "danger";
}) {
  const itemStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "8px 10px",
    background: "transparent",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 13,
    color: tone === "danger" ? "var(--danger)" : "var(--ink-1)",
    fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
    textAlign: "left",
  };
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      style={itemStyle}
    >
      <Icon name={icon} size={14} color={tone === "danger" ? "var(--danger)" : "var(--ink-3)"} />
      {label}
    </button>
  );
}
